// components/LayerPanel.tsx
import React, { useState } from "react";
import type { MapLayer } from "../types";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/store";
import AddLayerModal from "./AddLayerModal";
import CommonIcons from "../../../../../assets/icons";

interface LayerPanelProps
{
  layers: MapLayer[];
  onAdd: (layer: Omit<MapLayer, "id" | "zIndex">) => void;
  onRemove: (id: string) => void;
  onToggle: (id: string) => void;
  onOpacity: (id: string, v: number) => void;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
  onSetBasemap: (presetId: string) => void;
}

const TYPE_ICON: Record<string, string> = {
  osm: "🌍",
  xyz: "🗺",
  wms: "🔲",
  wmts: "⊞",
  geojson: "{}",
  wfs: "⬡",
  "vector-tile": "▦",
  image: "🖼",
};

const TYPE_COLOR: Record<string, string> = {
  osm: "#3fb37a",
  xyz: "#1e90ff",
  wms: "#ff922b",
  wmts: "#e8742e",
  geojson: "#51cf66",
  wfs: "#cc5de8",
  "vector-tile": "#8b5cf6",
  image: "#f06595",
};

const LayerPanel: React.FC<LayerPanelProps> = ({ layers, onAdd, onRemove, onToggle, onOpacity, onMoveUp, onMoveDown, onSetBasemap }) =>
{
  const { basemaps, selectedBasemap } = useSelector((state: RootState) => state.map);

  const [open, setOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showBasemap, setShowBasemap] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Display in reverse zIndex order (top layer first)
  const sorted = [...layers]?.sort((a, b) => b.zIndex - a.zIndex);

  return (
    <>
      {/* Panel toggle button */}
      <div
        style={{
          position: "absolute",
          bottom: "50px",
          right: "40px",
          zIndex: 1000,
        }}
      >
        <button
          onClick={() => setOpen((o) => !o)}
          title="Layer Manager"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "5px",
            background: "rgba(15,17,26,0.88)", // open ? "rgba(0,212,255,0.15)" : "rgba(15,17,26,0.88)",
            border: `1px solid ${open ? "rgba(0,212,255,0.4)" : "rgba(255,255,255,0.12)"}`,
            borderRadius: "6px",
            cursor: "pointer",
            color: open ? "#00d4ff" : "rgba(255,255,255,0.7)",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.5px",
            fontFamily: '"JetBrains Mono","Fira Code",monospace',
            backdropFilter: "blur(8px)",
            outline: "none",
            boxShadow: open ? "0 0 12px rgba(0,212,255,0.2)" : "none",
            transition: "all 0.15s",
          }}
        >
          {/* ☰ Layers{" "} */} <CommonIcons.BaseMapIcon />
          {/* {layers.length > 0 && (
            <span
              style={{
                background: "rgba(0,212,255,0.2)",
                color: "#00d4ff",
                borderRadius: "10px",
                padding: "0 5px",
                fontSize: "9px",
                fontWeight: 700,
              }}
            >
              {layers.filter((l) => l.visibility).length}/{layers.length}
            </span>
          )} */}
        </button>
      </div>

      {/* Panel body */}
      {open && (
        <div
          style={{
            position: "absolute",
            bottom: "46px",
            right: "84px",
            zIndex: 999,
            width: "300px",
            maxHeight: "calc(100vh - 100px)",
            background: "rgba(13,15,24,0.97)",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(255,255,255,0.10)",
            borderRadius: "14px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 12px 48px rgba(0,0,0,0.6)",
            fontFamily: '"JetBrains Mono","Fira Code",monospace',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 14px",
              borderBottom: "1px solid rgba(255,255,255,0.07)",
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "2px", color: "rgba(255,255,255,0.45)", textTransform: "uppercase" }}>
              {/* Layer Manager */} Base Map
            </span>
            <div style={{ display: "flex", gap: "4px" }}>
              {/* Basemap switcher */}
              {/* <button
                onClick={() => setShowBasemap((b) => !b)}
                title="Switch Basemap"
                style={{
                  padding: "4px 8px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "10px",
                  background: showBasemap ? "rgba(51,207,102,0.15)" : "rgba(255,255,255,0.06)",
                  border: `1px solid ${showBasemap ? "rgba(51,207,102,0.4)" : "rgba(255,255,255,0.1)"}`,
                  color: showBasemap ? "#51cf66" : "rgba(255,255,255,0.5)",
                  fontFamily: "inherit",
                  outline: "none",
                  transition: "all 0.15s",
                }}
              >
                🌍 Base
              </button> */}
              {/* Add layer */}
              {/* <button
                onClick={() => setShowModal(true)}
                title="Add Layer"
                style={{
                  padding: "4px 8px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "10px",
                  background: "rgba(0,212,255,0.12)",
                  border: "1px solid rgba(0,212,255,0.3)",
                  color: "#00d4ff",
                  fontFamily: "inherit",
                  outline: "none",
                }}
              >
                ＋ Add
              </button> */}
            </div>
          </div>

          {/* Basemap switcher */}
          {showBasemap && (
            <div
              style={{
                padding: "8px",
                borderBottom: "1px solid rgba(255,255,255,0.07)",
                display: "flex",
                flexDirection: "column",
                gap: "3px",
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  fontSize: "9px",
                  color: "rgba(255,255,255,0.3)",
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  marginBottom: "4px",
                }}
              >
                Select Basemap
              </div>
              {basemaps.map((p) =>
              {
                //const active = layers.some((l) => l.zIndex === 0 && (l.url === p.url || l.id === p.id));
                const active = selectedBasemap?.id === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() =>
                    {
                      onSetBasemap(p.id);
                      // setShowBasemap(false);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "6px 8px",
                      background: active ? "rgba(51,207,102,0.1)" : "transparent",
                      border: `1px solid ${active ? "rgba(51,207,102,0.3)" : "transparent"}`,
                      borderRadius: "6px",
                      cursor: "pointer",
                      textAlign: "left",
                      width: "100%",
                      color: active ? "#51cf66" : "rgba(255,255,255,0.55)",
                      fontSize: "11px",
                      fontFamily: "inherit",
                      outline: "none",
                    }}
                  >
                    <span style={{ opacity: 0.6 }}>🌍</span>
                    <span style={{ flex: 1 }}>{p.name}</span>
                    {active && <span style={{ fontSize: "9px" }}>✓</span>}
                  </button>
                );
              })}
            </div>
          )}

          {/* Layer list */}
          {/* <div style={{ overflowY: "auto", flex: 1 }}>
            {sorted.length === 0 && (
              <div style={{ padding: "20px", textAlign: "center", color: "rgba(255,255,255,0.2)", fontSize: "11px" }}>
                No layers. Click ＋ Add to get started.
              </div>
            )}

            {sorted.map((layer) =>
            {
              const origIdx = layers.findIndex((l) => l.id === layer.id);
              const isTop = origIdx === layers.length - 1;
              const isBottom = origIdx === 0;
              const isExp = expandedId === layer.id;
              const tc = TYPE_COLOR[layer.type] ?? "#aaa";

              return (
                <div
                  key={layer.id}
                  style={{
                    borderBottom: "1px solid rgba(255,255,255,0.04)",
                    background: isExp ? "rgba(255,255,255,0.03)" : "transparent",
                  }}
                >
                  
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "7px 10px",
                      cursor: "pointer",
                      opacity: layer.visibility ? 1 : 0.45,
                    }}
                  >
                
                    <span
                      style={{
                        width: "22px",
                        height: "22px",
                        borderRadius: "4px",
                        flexShrink: 0,
                        background: `${tc}18`,
                        border: `1px solid ${tc}40`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "10px",
                        color: tc,
                      }}
                    >
                      {TYPE_ICON[layer.type] ?? "?"}
                    </span>

                    <button
                      onClick={() => onToggle(layer.id)}
                      title="Toggle visibility"
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "13px",
                        padding: 0,
                        lineHeight: 1,
                        flexShrink: 0,
                        color: layer.visibility ? "#00d4ff" : "rgba(255,255,255,0.2)",
                        outline: "none",
                      }}
                    >
                      {layer.visibility ? "👁" : "🚫"}
                    </button>

           
                    <span
                      onClick={() => setExpandedId(isExp ? null : layer.id)}
                      style={{
                        flex: 1,
                        fontSize: "11px",
                        color: "rgba(255,255,255,0.75)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                      }}
                      title={layer.name}
                    >
                      {layer.name}
                    </span>

               
                    <button
                      onClick={() => onMoveUp(layer.id)}
                      disabled={isTop}
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: isTop ? "default" : "pointer",
                        color: isTop ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.4)",
                        fontSize: "11px",
                        padding: "0 2px",
                        outline: "none",
                      }}
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => onMoveDown(layer.id)}
                      disabled={isBottom}
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: isBottom ? "default" : "pointer",
                        color: isBottom ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.4)",
                        fontSize: "11px",
                        padding: "0 2px",
                        outline: "none",
                      }}
                    >
                      ↓
                    </button>

               
                    <button
                      onClick={() => onRemove(layer.id)}
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        color: "rgba(255,80,80,0.4)",
                        fontSize: "12px",
                        padding: "0 2px",
                        lineHeight: 1,
                        outline: "none",
                        transition: "color 0.15s",
                      }}
                      onMouseEnter={(e) =>
                      {
                        e.currentTarget.style.color = "#ff6b6b";
                      }}
                      onMouseLeave={(e) =>
                      {
                        e.currentTarget.style.color = "rgba(255,80,80,0.4)";
                      }}
                    >
                      ✕
                    </button>
                  </div>

                 
                  {isExp && (
                    <div style={{ padding: "0 10px 10px 40px", display: "flex", flexDirection: "column", gap: "8px" }}>
                    
                      <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <span
                          style={{
                            fontSize: "9px",
                            color: "rgba(255,255,255,0.35)",
                            letterSpacing: "1px",
                            textTransform: "uppercase",
                            fontWeight: 700,
                          }}
                        >
                          Opacity — {Math.round(layer.opacity * 100)}%
                        </span>
                        <input
                          type="range"
                          min={0}
                          max={1}
                          step={0.01}
                          value={layer.opacity}
                          onChange={(e) => onOpacity(layer.id, parseFloat(e.target.value))}
                          style={{ width: "100%", accentColor: "#00d4ff", cursor: "pointer" }}
                        />
                      </label>

         
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                        <span
                          style={{
                            fontSize: "9px",
                            padding: "2px 7px",
                            borderRadius: "10px",
                            background: `${tc}18`,
                            border: `1px solid ${tc}30`,
                            color: tc,
                            letterSpacing: "0.5px",
                            textTransform: "uppercase",
                          }}
                        >
                          {layer.type.toUpperCase()}
                        </span>
                        <span
                          style={{
                            fontSize: "9px",
                            padding: "2px 7px",
                            borderRadius: "10px",
                            background: "rgba(255,255,255,0.06)",
                            border: "1px solid rgba(255,255,255,0.1)",
                            color: "rgba(255,255,255,0.4)",
                          }}
                        >
                          z={layer.zIndex}
                        </span>
                      </div>

                      {layer.url && (
                        <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.25)", wordBreak: "break-all", lineHeight: 1.5 }}>
                          {layer.url.length > 60 ? layer.url.slice(0, 60) + "…" : layer.url}
                        </div>
                      )}
                      {layer.wmsParams?.layers && (
                        <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.25)" }}>Layers: {String(layer.wmsParams.layers)}</div>
                      )}
                      {layer.type === "cog" && layer.url && (
                        <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.25)", wordBreak: "break-all" }}>
                          COG:{" "}
                          {(() =>
                          {
                            const u = String(layer.url);
                            return u.length > 60 ? u.slice(0, 60) + "…" : u;
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div> */}

          {/* Footer */}
          <div
            style={{
              padding: "6px 14px",
              borderTop: "1px solid rgba(255,255,255,0.07)",
              fontSize: "9px",
              color: "rgba(255,255,255,0.2)",
              letterSpacing: "0.5px",
              flexShrink: 0,
            }}
          >
            {layers.filter((l) => l.visibility).length} visible · {layers.length} total
          </div>
        </div>
      )}
      {showModal && <AddLayerModal onAdd={onAdd} onClose={() => setShowModal(false)} />}
    </>
  );
};

export default LayerPanel;
